import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
    indexEmployeePagination,
    getIstQuarter,
    getIIndQuarter,
    getIIIrdQuarter,
    getIVthQuarter,
    getAssessmentRegime,
    getSchoolYear
} from '../../Api'

import {
    Container,
    List,
    Emp,
    EmpMain,
    Avatar,
    Span,
    StatusBadge,
    Search,
    DivNewEmp,
    FormFilter,
    FormSearch,
    Area,
    Select,
    InputEmp,
    Btt02,
    SkeletonRow,
    LoadMoreText,
    EmptyState,
} from './style';

/* iniciais para o avatar */
const initials = (n = '') =>
    n.trim().split(/\s+/).slice(0, 2).map(p => p[0] || '').join('') || '?';

const LIMIT = 20;

const Employees = () => {

    const navigate = useNavigate()

    const [employees, setEmployees] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)

    const [positionAtEducationDepartment, setPositionAtEducationDepartment] = useState('')
    const [busca, setBusca] = useState("")
    const [debouncedBusca, setDebouncedBusca] = useState("")
    const [filter, setFilter] = useState(sessionStorage.getItem("selectedFilter") || "")

    const [loading, setLoading] = useState(true)        // skeleton cheio: SÓ 1ª carga
    const [listLoading, setListLoading] = useState(false) // refetch (filtro/busca)
    const [loadingMore, setLoadingMore] = useState(false) // scroll infinito

    const [showModal, setShowModal] = useState(false);
    const [missingPeriods, setMissingPeriods] = useState([]); // labels dos períodos que faltam
    const [hasNoPeriods, setHasNoPeriods] = useState(false);  // true quando nenhum período existe

    const idSchool = JSON.parse(sessionStorage.getItem("id-school"))

    const initializedRef = useRef(false)
    const fetchingRef = useRef(false)
    const sentinelRef = useRef(null)

    // 🔹 Debounce da busca
    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 400)
        return () => clearTimeout(t)
    }, [busca])

    // 🔹 Trava o scroll da página enquanto o aviso está aberto.
    //    Sempre restaura para '' (destravado) — nunca propaga um 'hidden' preso.
    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => { document.body.style.overflow = '' }
    }, [showModal])

    // 🔹 Busca paginada no backend
    const fetchEmployees = useCallback(async (pageToLoad, reset) => {
        if (fetchingRef.current) return
        fetchingRef.current = true
        try {
            const res = await indexEmployeePagination({
                idSchool,
                page: pageToLoad,
                limit: LIMIT,
                filter,
                search: debouncedBusca
            })

            const { data, hasMore: more, total: t } = res.data

            setTotal(t ?? 0)
            setHasMore(Boolean(more))
            setEmployees(prev => (reset ? data : [...prev, ...data]))
        } finally {
            fetchingRef.current = false
        }
    }, [idSchool, filter, debouncedBusca])

    // 🔹 Setup inicial: cargo do usuário + checagem do 1º período (modal)
    useEffect(() => {
        (async () => {
            const pos = localStorage.getItem("positionAtEducationDepartment")
            setPositionAtEducationDepartment(pos)

            // 🔥 Verificação dos períodos avaliativos (não bloqueia a lista)
            try {
                const schoolYear = await getSchoolYear(idSchool)
                const year = schoolYear.data.data

                const [regimeRes, q1, q2, q3, q4] = await Promise.all([
                    getAssessmentRegime(idSchool),
                    getIstQuarter(year, idSchool),
                    getIIndQuarter(year, idSchool),
                    getIIIrdQuarter(year, idSchool),
                    getIVthQuarter(year, idSchool),
                ])

                // existe = a resposta do quarter traz ao menos um registro
                const exists = (res) => (res?.data?.data?.length > 0)

                const quarters = [
                    { label: '1º Período', ok: exists(q1) },
                    { label: '2º Período', ok: exists(q2) },
                    { label: '3º Período', ok: exists(q3) },
                    { label: '4º Período', ok: exists(q4) },
                ]

                // regime: usa getAssessmentRegime; se não vier, tenta inferir pelo 1º período existente
                let regime = regimeRes?.data?.data || null
                if (!regime) {
                    const firstExisting = [q1, q2, q3, q4].find(exists)
                    regime = firstExisting?.data?.data?.find(res => res)?.assessmentRegime || null
                }

                // trimestral = 3 períodos; qualquer outro caso trata como bimestral (4)
                const isTrimestral = regime === 'TRIMESTRAL'
                const required = isTrimestral ? 3 : 4

                const requiredQuarters = quarters.slice(0, required)
                const missing = requiredQuarters.filter(q => !q.ok).map(q => q.label)
                const noneRegistered = requiredQuarters.every(q => !q.ok)

                if (missing.length > 0) {
                    setMissingPeriods(missing)
                    setHasNoPeriods(noneRegistered)
                    setShowModal(true)
                }
            } catch (e) {
                console.error("Erro ao verificar períodos avaliativos:", e)
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // 🔹 Recarrega da página 1 quando cargo/busca mudam
    //    1ª vez: skeleton cheio | depois: input NÃO desmonta, só aviso na lista
    useEffect(() => {
        (async () => {
            if (!initializedRef.current) {
                setLoading(true)
            } else {
                setListLoading(true)
            }
            setPage(1)
            await fetchEmployees(1, true)
            setLoading(false)
            setListLoading(false)
            initializedRef.current = true
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, debouncedBusca])

    const loadMore = useCallback(async () => {
        if (fetchingRef.current || !hasMore) return
        const next = page + 1
        setLoadingMore(true)
        setPage(next)
        await fetchEmployees(next, false)
        setLoadingMore(false)
    }, [page, hasMore, fetchEmployees])

    // 🔹 Scroll infinito
    useEffect(() => {
        const node = sentinelRef.current
        if (!node || !hasMore || loading) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMore()
            },
            { rootMargin: '300px' }
        )

        observer.observe(node)
        return () => observer.disconnect()
    }, [hasMore, loading, loadMore])

    const NewEmoloyee = async () => {
        navigate('/new/employees')
    }

    const employeeInformation = async (employee) => {
        navigate(`/employee/info/${employee._id}`)
    }

    const handleCloseModal = () => {
        setShowModal(false)
    }

    return (
        <Container>
            {loading ? (
                <>
                    <Search>
                        <FormSearch>
                            <label>Buscar Funcionário</label>
                            <Area>
                                <InputEmp type="text" placeholder="Buscar" disabled />
                            </Area>
                        </FormSearch>
                        <FormFilter>
                            <label>Filtrar por cargo</label>
                            <Select disabled><option>Todos</option></Select>
                        </FormFilter>
                    </Search>
                    <List>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <SkeletonRow key={i}>
                                <span className="circle" />
                                <span className="bar" />
                                <span className="pill" />
                            </SkeletonRow>
                        ))}
                    </List>
                </>
            ) : (
                <>
                    <Search>
                        <FormSearch>
                            <label>Buscar Funcionário</label>
                            <Area>
                                <InputEmp
                                    type="text"
                                    placeholder='Buscar pelo nome'
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                />
                            </Area>
                        </FormSearch>
                        <FormFilter>
                            <label>Filtrar por cargo</label>
                            <Select id="position"
                                value={filter}
                                onChange={(e) => {
                                    setFilter(e.target.value)
                                    sessionStorage.setItem("selectedFilter", e.target.value)
                                }}
                            >
                                <option value="">Todos</option>
                                <option value="DIRETOR/SUPERVISOR">DIRETOR(A)/SUPERVISOR(A)</option>
                                <option value="SECRETARIO">SECRETARIO(A)</option>
                                <option value="PROFESSOR">PROFESSOR(A)</option>
                            </Select>
                        </FormFilter>
                    </Search>

                    {!positionAtEducationDepartment &&
                        <DivNewEmp>
                            <Btt02 onClick={NewEmoloyee}>Novo Funcionário</Btt02>
                        </DivNewEmp>
                    }
                    {positionAtEducationDepartment && total <= 0 &&
                        <DivNewEmp>
                            <Btt02 onClick={() => navigate('/first/employee')}>Primeiro Funcionário</Btt02>
                        </DivNewEmp>
                    }

                    <List>
                        <p>
                            Total de funcionários cadastrados: <em>{total}</em>
                            {listLoading && " — atualizando..."}
                        </p>

                        {employees.length === 0 && !listLoading ? (
                            <EmptyState>
                                <span className="em">🔍</span>
                                Nenhum funcionário encontrado.
                            </EmptyState>
                        ) : (
                            employees.map(employee => {
                                const active = employee.status !== "inactive";
                                return (
                                    <Emp
                                        onClick={() => employeeInformation(employee)}
                                        key={employee._id}
                                    >
                                        <EmpMain>
                                            <Avatar>{initials(employee.name)}</Avatar>
                                            <Span>{employee.name}</Span>
                                        </EmpMain>
                                        <StatusBadge $active={active}>
                                            {active ? "Ativo" : "Inativo"}
                                        </StatusBadge>
                                    </Emp>
                                );
                            })
                        )}

                        {/* sentinela do scroll infinito */}
                        <div ref={sentinelRef} style={{ height: 1 }} />

                        {loadingMore && (
                            <LoadMoreText>Carregando mais funcionários...</LoadMoreText>
                        )}
                    </List>
                </>
            )}

            {showModal && createPortal(
                <div
                    onClick={handleCloseModal}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0, 30, 38, 0.55)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2147483647,
                        padding: 16,
                        boxSizing: 'border-box',
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: 'var(--surface)',
                            borderRadius: 16,
                            padding: '26px 24px',
                            width: '100%',
                            maxWidth: 460,
                            boxSizing: 'border-box',
                            boxShadow: '0 18px 50px rgba(0,0,0,0.25)',
                            textAlign: 'center',
                        }}
                    >
                        <h3 style={{ margin: '0 0 12px', fontSize: 18, color: '#cf3a3a' }}>
                            ⚠️ Atenção
                        </h3>
                        {hasNoPeriods ? (
                            <p style={{ margin: '0 0 20px', color: '#5b6b72', fontSize: 14, lineHeight: 1.55, textAlign: 'left' }}>
                                Ainda não existe período avaliativo registrado.
                                <br /><br />
                                É necessário registrar os períodos avaliativos deste ano para que o diário dos professores funcione corretamente.
                                <br /><br />
                                Vá em <strong>Períodos Avaliativos</strong> e adicione todos os períodos do ano.
                            </p>
                        ) : (
                            <div style={{ margin: '0 0 20px', color: '#5b6b72', fontSize: 14, lineHeight: 1.55, textAlign: 'left' }}>
                                <p style={{ margin: '0 0 10px' }}>
                                    Ainda faltam registrar os seguintes períodos avaliativos:
                                </p>
                                <ul style={{
                                    listStyle: 'none',
                                    margin: '0 0 12px',
                                    padding: '10px 12px',
                                    border: '1px solid #cf3a3a',
                                    borderLeft: '4px solid #cf3a3a',
                                    borderRadius: 8,
                                    background: '#fbe9e9',
                                }}>
                                    {missingPeriods.map((label) => (
                                        <li key={label} style={{ color: '#cf3a3a', fontWeight: 700, fontSize: 13.5, padding: '3px 0' }}>
                                            ● {label}
                                        </li>
                                    ))}
                                </ul>
                                <p style={{ margin: 0 }}>
                                    Vá em <strong>Período Avaliativo</strong> e adicione os períodos que faltam para o funcionamento correto do sistema.
                                </p>
                            </div>
                        )}
                        <button
                            onClick={handleCloseModal}
                            style={{
                                padding: '11px 28px',
                                border: 'none',
                                borderRadius: 10,
                                background: '#003e4f',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 700,
                                fontFamily: 'inherit',
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </Container>
    )
}

export default Employees